import { useEffect, useState } from 'react'
import { SlideOver } from './SlideOver.js'
import { fetchPoPreview, raisePurchaseOrder, type PoPreview, type PurchaseOrderView } from '../lib/api.js'

/**
 * Decision 213 — the "Raise purchase order" panel. Opens on a supplier quote,
 * shows exactly what Xero will receive (the quote's CURRENT quantities and
 * costs, the contact, the nominal, the reference), the existing draft if one
 * was raised before, and — before the button — what needs a human's eye:
 * a quote that isn't accepted (Mark's answer #3: allowed, warned), £0 lines.
 * Blockers (no contact set, not connected, PO past DRAFT in Xero) disable
 * the button and say why. Nothing is written until Raise is pressed.
 */

const gbp = (n: number) => `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

export function RaisePurchaseOrder({ supplierQuoteId, title, onClose, onRaised }: {
  supplierQuoteId: string
  title: string
  onClose: () => void
  onRaised: (po: PurchaseOrderView, action: 'created' | 'updated') => void
}) {
  const [preview, setPreview] = useState<PoPreview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<{ po: PurchaseOrderView; action: 'created' | 'updated' } | null>(null)

  useEffect(() => {
    setPreview(null); setError(null)
    fetchPoPreview(supplierQuoteId).then(setPreview).catch((e: Error) => setError(e.message))
  }, [supplierQuoteId])

  const raise = async () => {
    setBusy(true); setError(null)
    try {
      const r = await raisePurchaseOrder(supplierQuoteId)
      setDone({ po: r.po, action: r.action })
      onRaised(r.po, r.action)
    } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }

  const blocked = !preview || preview.blockers.length > 0
  const verb = preview?.existing?.canUpdate ? 'Update the draft PO' : 'Raise draft PO'

  return (
    <SlideOver title={`Purchase order — ${title}`} onClose={onClose} wide
      subtitle={preview ? `${preview.distributor} · ${preview.quoteNumber} ${preview.quoteTitle}` : undefined}>
      {error && <div className="banner banner--stop" data-testid="po-error"><span style={{ whiteSpace: 'pre-line' }}>{error}</span><button className="btn btn--ghost btn--sm" onClick={() => setError(null)}>Dismiss</button></div>}
      {!preview && !error && <p className="muted">Reading the quote and checking Xero…</p>}
      {preview && (
        <>
          {done && (
            <div className="banner" data-testid="po-done">
              <span>
                {done.action === 'created' ? 'Draft purchase order raised' : 'Draft purchase order updated'}
                {done.po.xeroPoNumber ? <> — <strong>{done.po.xeroPoNumber}</strong></> : null}.{' '}
                <a href={done.po.url} target="_blank" rel="noreferrer">Open in Xero →</a> — review, then approve and send it from there.
              </span>
            </div>
          )}
          {preview.blockers.map((b) => <div key={b} className="banner banner--stop" data-testid="po-blocker"><span>{b}</span></div>)}
          {preview.warnings.map((w) => <div key={w} className="banner" data-testid="po-warning"><span>⚠ {w}</span></div>)}

          <dl className="kv" style={{ marginBottom: 14 }}>
            <dt>To</dt><dd>{preview.settings.contactName || <span className="muted">no supplier contact set</span>}</dd>
            <dt>Reference</dt><dd>{preview.reference}</dd>
            <dt>Customer</dt><dd>{preview.customerName ?? <span className="muted">—</span>} <span className="muted small">(delivery instructions; address = Xero's default)</span></dd>
            <dt>Status</dt><dd>DRAFT · GBP · amounts ex VAT · tax {preview.settings.taxType || '—'} · purchase nominal per line (default {preview.settings.accountCode || '—'} for lines with no sales nominal)</dd>
            {preview.existing && (
              <>
                <dt>Existing PO</dt>
                <dd>
                  <a href={preview.existing.url} target="_blank" rel="noreferrer"><strong>{preview.existing.xeroPoNumber ?? preview.existing.xeroPoId}</strong></a>{' '}
                  <span className={`pill ${preview.existing.canUpdate ? 'pill--info' : 'pill--warn'}`}>{preview.existing.xeroStatus}</span>{' '}
                  <span className="muted small">raised {new Date(preview.existing.raisedAt).toLocaleDateString('en-GB')} by {preview.existing.raisedByName}</span>
                  {preview.existing.canUpdate && <div className="muted small">Still a draft in Xero — raising again rewrites its lines with the quote as it is now.</div>}
                </dd>
              </>
            )}
          </dl>

          <div className="table-wrap">
            <table data-testid="po-lines">
              <thead><tr><th>SKU</th><th>Description</th><th>Nominal</th><th style={{ textAlign: 'right' }}>Qty</th><th style={{ textAlign: 'right' }}>Unit cost</th><th style={{ textAlign: 'right' }}>Line (ex VAT)</th></tr></thead>
              <tbody>
                {preview.lines.map((l) => (
                  <tr key={l.quoteItemId}>
                    <td><code>{l.sku}</code></td>
                    <td>{l.description}</td>
                    <td style={{ whiteSpace: 'nowrap' }} data-testid="po-line-nominal">
                      {l.accountCode
                        ? <>{l.accountCode}{l.salesNominal ? <span className="muted small"> ← {l.salesNominal}</span> : <span className="muted small"> (default)</span>}</>
                        : <span className="pill pill--stop" title={`Sales nominal ${l.salesNominal} is not mapped`}>{l.salesNominal} unmapped</span>}
                    </td>
                    <td style={{ textAlign: 'right' }}>{l.qty}</td>
                    <td style={{ textAlign: 'right' }}>{gbp(l.unitCost)}</td>
                    <td style={{ textAlign: 'right' }}>{gbp(l.lineTotal)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr><td colSpan={5}><strong>{preview.lines.length} lines · sub-total ex VAT</strong></td><td style={{ textAlign: 'right' }}><strong>{gbp(preview.subTotal)}</strong></td></tr></tfoot>
            </table>
          </div>
          <p className="muted small" style={{ marginTop: 8 }}>
            Quantities and unit costs are the quote's as of now (a distributor re-quote applied to the draft is already reflected).
            Lines removed from the quote are not ordered. Xero assigns the PO number.
          </p>

          <div className="toolbar" style={{ marginTop: 14 }}>
            <button className="btn btn--primary" disabled={blocked || busy || done !== null} onClick={() => void raise()} data-testid="po-raise">
              {busy ? 'Sending to Xero…' : verb}
            </button>
            <button className="btn btn--ghost" onClick={onClose}>{done ? 'Close' : 'Cancel'}</button>
          </div>
        </>
      )}
    </SlideOver>
  )
}
